import {
    isArrayValid
} from '@apricityhealth/web-common-lib/utils/Services';
import { reject } from 'lodash';

import languages from '../data/Languages.json';
import QuestionsDataTypes from './QuestionDataTypes'

const Promise = require('bluebird');
export default class QuestionUtils {
    constructor(appContext, language = null, texts = null) {
        if (!appContext || !appContext.state) throw new Error("QuestionUtils needs a valid appContext with state.");
        if (!language) language = appContext.state.language || 'en-us';
        if (!texts) texts = appContext.stores.DataTypesStore.getTextHash();

        this.state = {
            appContext,
            language,
            texts
        }
    }

    getText(textId, language = null) {
        //console.log(`getText ${textId}, language: ${language}:`, this );
        const { texts } = this.state;
        if (!language) language = this.state.language || 'en-us';
        if (texts[language]) {
            if (texts[language][textId]) {
                return texts[language][textId]
            }
        }
        return {
            textId,
            language,
            text: ``,
            version: 0,
            category: 'dialog'
        };
    }

    unlocalizeQuestion(question) {
        try {
            //this will convert all the textId references and make them actual text for the purpose of editing.
            for (let i = 0; i < question.question.length; ++i) {
                if ( question.question[i].text ) question.question[i].text = this.getText(i > 0 ? question.questionId + `.${i}` : question.questionId).text;
            }
            question.explanation = this.getText(question.questionId + '.explanation').text;
            question.instructions = this.getText(question.questionId + '.instructions').text;
            for (let i = 0; i < question.answers.length; ++i) {
                let answer = question.answers[i];
                let textId = question.questionId + '.' + answer.answerId;

                answer.explanation = this.getText(textId + '.explanation').text;
                answer.category = this.getText(textId + ".category").text;
                for (let j = 0; j < answer.text.length; ++j) {
                    answer.text[j] = this.getText(j > 0 ? textId + `.${j}` : textId).text;
                }
            }
        }
        catch (err) {
            console.error("unlocalizeQuestion error:", err);
            throw err;
        }
        return question;
    }


    deleteQuestion(question, cleanText = true) {
        const { appContext } = this.state;
        const store = appContext.stores.DataTypesStore;
        let promises = [
            store.deleteQuestion(question.questionId)
        ];
        const { planId } = question;

        if (cleanText) {
            //delete all text content. This will delete all languages as well.
            for (let i = 0; i < question.question.length; ++i) {
                if (question.question[i]) {
                    let textId = i > 0 ? `${question.questionId}.${i}` : `${question.questionId}`;
                    promises.push(store.deleteText(textId, null, planId));
                }
            }

            //delete all text explanation content
            if (question.explaination)
                promises.push(store.deleteText(question.questionId + ".explanation", null, planId));

            //delete all text instructions content
            if (question.instructions)
                promises.push(store.deleteText(question.questionId + ".instructions", null, planId));

            let answers = question.answers;
            if (isArrayValid(answers)) {
                answers.forEach(answer => {
                    let answerId = answer.answerId;
                    for (let i = 0; i < answer.text.length; ++i) {
                        if (answer.text[i]) {
                            let textId = i > 0 ? `${question.questionId}.${answerId}.${i}` : `${question.questionId}.${answerId}`;
                            promises.push(store.deleteText(textId, null, planId));
                        }
                    }
                    //delete all explanation text content
                    if (answer.explanation)
                        promises.push(store.deleteText(question.questionId + '.' + answerId + ".explanation", null, planId));
                    if (answer.category)
                        promises.push(store.deleteText(question.questionId + '.' + answerId + ".category", null, planId));
                });
            }
        }

        return Promise.all(promises);
    }

    saveQuestion = async (question, originalQuestion = null, planId = null, updateDataTypes = true) => {
        const self = this;          // keep self, it's used inside the localilzeText function
        const { appContext, language } = this.state;
        const store = appContext.stores.DataTypesStore;

        if (!planId) {
            if (!appContext.state.plan) return reject(new Error("No valid plan selected!"));
            planId = appContext.state.plan.planId;
        }
        // firstly, make a deep copy of the question, I've run into problems with the localization
        // text strings getting saved into the question edit boxes, and I think it's because we were
        // modifying the strings of the question before saving, if anything went wrong then 
        // we could end up saving the string keys into the text, which was causing an infinite loop
        // and we would lose all the text.
        question = JSON.parse(JSON.stringify(question));
        if (!originalQuestion) originalQuestion = JSON.parse(JSON.stringify(question));
        const originalQuestionId = originalQuestion.questionId;
        // Renaming a question (questionId changed) must leave the original question
        // and all of its text records completely untouched — the renamed save is
        // treated as a brand-new question. The user must open the original and
        // delete it explicitly if they want it gone.
        const idChanged = originalQuestionId !== question.questionId;

        let promises = [];

        function localizeText(localize, oldId, newId) {
            // On rename, pretend no prior record exists under oldId so we never
            // mutate/rename or delete the original question's text records.
            const effectiveOldId = idChanged ? newId : oldId;
            if (localize) {
                const text = self.getText(effectiveOldId);
                //if id has changed or text has changed
                if (localize !== text.text || effectiveOldId !== newId || text.planId !== planId) {
                    //update version if text has changed - new ids do not bump versions
                    if (localize !== text.text) {
                        text.version = text.version + 1;
                    }
                    text.text = localize;
                    text.textId = newId
                    promises.push(store.saveText(text, planId));

                    for (let lang in languages) {
                        if (lang !== language) {
                            const langText = self.getText(effectiveOldId, lang);
                            if (langText.text !== '') {
                                langText.textId = newId;
                                langText.version = text.version;
                                promises.push(store.saveText(langText, planId));
                            }
                        }
                    }
                }
                return `[[${newId}]]`;
            } else {
                // On rename the original question's records must remain — skip the delete.
                if (oldId && !idChanged) {
                    // only delete language specific strings in this case, since we may be editing a question with no language translation are we don't want it to delete the en-us
                    if (language !== 'en-us')
                        promises.push(store.deleteText(oldId, language, planId));
                    else
                        promises.push(store.deleteText(oldId, null, planId));
                }
                return '';
            }
        }

        for (let i = 0; i < question.question.length; ++i) {
            let oldId = i > 0 ? originalQuestionId + `.${i}` : originalQuestionId;
            let newId = i > 0 ? question.questionId + `.${i}` : question.questionId;
            question.question[i].text = localizeText(question.question[i].text, oldId, newId);
        }

        // Pruning trailing question-text entries only applies to the original question;
        // on rename those records still belong to it and must be left alone.
        if (!idChanged && originalQuestion.question.length > question.question.length) {
            for (let i = question.question.length; i < originalQuestion.question.length; ++i) {
                let oldId = i > 0 ? originalQuestionId + `.${i}` : originalQuestionId;
                promises.push(store.deleteText(oldId, null, originalQuestion.planId));
            }
        }

        question.explanation = localizeText(question.explanation, originalQuestionId + '.explanation', question.questionId + '.explanation');
        question.instructions = localizeText(question.instructions, originalQuestionId + '.instructions', question.questionId + '.instructions');

        for (let i = 0; i < question.answers.length; ++i) {
            let answer = question.answers[i];
            // Match by _originalAnswerId (set at load time) so reorders and answerId renames don't misalign
            // text records; fall back to current answerId so new answers don't accidentally match an original.
            let lookupId = answer._originalAnswerId || answer.answerId;
            let originalAnswer = originalQuestion.answers.find((a) => a.answerId === lookupId) || null;

            let oldId = originalAnswer ? originalQuestionId + '.' + originalAnswer.answerId : originalQuestionId + '.' + answer.answerId;
            let newId = question.questionId + '.' + answer.answerId;

            for (let j = 0; j < answer.text.length; ++j) {
                let oldAnswerTextId = j > 0 ? oldId + `.${j}` : oldId;
                let newAnswerTextId = j > 0 ? newId + `.${j}` : newId;
                answer.text[j] = localizeText(answer.text[j], oldAnswerTextId, newAnswerTextId);
            }

            answer.explanation = localizeText(answer.explanation, oldId + ".explanation", newId + ".explanation");
            answer.category = localizeText(answer.category, oldId + ".category", newId + ".category");

            // Pruning trailing answer-text entries targets the original question's
            // records; skip on rename so the original keeps its text intact.
            if (!idChanged && originalAnswer !== null && originalAnswer.text.length > answer.text.length) {
                for (let j = answer.text.length; j < originalAnswer.text.length; ++j) {
                    let oldAnswerTextId = j > 0 ? oldId + `.${j}` : oldId;
                    promises.push(store.deleteText(oldAnswerTextId, null, planId));
                }
            }
        }

        // Removed-answer cleanup only applies when the questionId hasn't changed —
        // otherwise these records still belong to the (now separate) original question.
        if (!idChanged) {
            const trackedAnswerIds = new Set(question.answers.map((a) => a._originalAnswerId || a.answerId));
            for (let i = 0; i < originalQuestion.answers.length; ++i) {
                const origAnswer = originalQuestion.answers[i];
                if (!trackedAnswerIds.has(origAnswer.answerId)) {
                    let oldId = originalQuestionId + '.' + origAnswer.answerId;
                    for (let j = 0; j < origAnswer.text.length; ++j) {
                        let oldAnswerTextId = j > 0 ? oldId + `.${j}` : oldId;
                        promises.push(store.deleteText(oldAnswerTextId, null, planId));
                    }
                    promises.push(store.deleteText(oldId + ".explanation", null, planId));
                    promises.push(store.deleteText(oldId + ".category", null, planId));
                }
            }
        }

        // Strip internal tracking field before persisting to the backend.
        for (let i = 0; i < question.answers.length; ++i) {
            delete question.answers[i]._originalAnswerId;
        }
        promises.push(store.saveQuestions(question, planId));
        let results = await Promise.all(promises);
        if (updateDataTypes === true) {
            console.debug(`Updating data types for question`)
            results = await QuestionsDataTypes.updateDataTypesForQuestion(this.state.appContext, question);
        }
        return results;
    }

    async saveQuestions(questions, planId = null, updateDataTypes = true) {
        return await Promise.map(questions, (question) => this.saveQuestion(question, null, planId, updateDataTypes), { concurrency: 1 });
    }
}
